import React, { Suspense } from 'react';
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { loadQuery, RelayEnvironmentProvider } from 'react-relay';
import { RelayEnvironment } from '../relay/RelayEnvironment';
import '../../../stylesheets/components/globals.module.scss';
import '../../../stylesheets/components/theme.module.scss';
import '../../../stylesheets/components/buttonGlobals.module.scss';
import '../../../stylesheets/components/dropdownMenus.module.scss';
import '../../../stylesheets/components/dialog.module.scss';
import Email from './Email';
import Options from './Options';
import ActivateEmail, {
  EmailQuery as ActivateEmailQuery,
} from './activate/Email';
import Activate, { ActivateGlimmer, ActivateQuery } from './activate/Activate';
import ActivateOptions from './activate/Options';
import ForgotPassword from './ForgotPassword';
import ChangePassword, {
  ChangePasswordGlimmer,
  ChangePasswordQuery,
} from './ChangePassword';
import SignupAgreement from './SignupAgreement';
import RequestLink, { RequestLinkQuery } from './link/RequestLink';
import LinkAccount, { LinkAccountQuery } from './link/LinkAccount';
import { GlobalContextProvider } from '../components/GlobalContextProvider';
import LoginBody from './LoginBody';
import PageHeader from '../tenant_portal/shared/PageHeader';
import Logout from './Logout';
import { LoginHeaderQuery } from './LoginHeader';
import ChooseRole, { ChooseRoleGlimmer, ChooseRoleQuery } from './ChooseRole';
import CreateAccount from './create_account/CreateAccount';
import CreateAccountEmailPreloader, {
  CreateAccountPrefillQuery,
} from './create_account/EmailPreloader';
import SubmitSpinner from '../components/SubmitSpinner';
import { renderReactRoot } from '../components/renderReactRoot';
import loginStyles from '../../../stylesheets/components/login.module.scss';
import {
  getIntentLoginPathForPathname,
  isFreeTrialPathname,
} from './RouteIntents';

const rootElement: Element | null = document.querySelector('#root');

const LoginOptionsPage = () => {
  const location = useLocation();

  return (
    <div className={loginStyles.loginRouteContent}>
      <PageHeader
        title="Welcome back"
        dataTestClass="login-page-header"
        includeBottomMargin={false}
      />
      <Options
        emailPath="email"
        existingAccountActionTo={getIntentLoginPathForPathname(
          location.pathname
        )}
      />
    </div>
  );
};

const buildIntentLoginChildren = () => {
  return [
    {
      index: true,
      element: <LoginOptionsPage />,
    },
    {
      path: 'email',
      element: <Email />,
    },
    {
      path: 'forgot-password',
      element: <ForgotPassword />,
    },
  ];
};

const Login = () => {
  return (
    <GlobalContextProvider isLoggedIn={false} syncHtmlThemeDark1={true}>
      <main className={loginStyles.loginWrap}>
        <LoginBody>
          <Outlet />
        </LoginBody>
      </main>
    </GlobalContextProvider>
  );
};

const Root = () => {
  return (
    <RelayEnvironmentProvider environment={RelayEnvironment}>
      <Login />
    </RelayEnvironmentProvider>
  );
};

const loginRootLoader = () => {
  const hostParts = window.location.host.split('.');
  let subdomain = '';
  if (hostParts.length > 2) {
    const sd = hostParts[0];
    if (sd !== 'www' && sd !== 'app') subdomain = sd;
  }
  return loadQuery(RelayEnvironment, LoginHeaderQuery, {
    subdomain: subdomain,
  });
};

const createAccountEmailLoader = ({ request }: { request: Request }) => {
  const url = new URL(request.url);
  const email = url.searchParams.get('email') || '';
  const variables = isFreeTrialPathname(url.pathname)
    ? { email }
    : { encryptedEmail: email };

  return loadQuery(RelayEnvironment, CreateAccountPrefillQuery, variables);
};

const createAccountEmailElement = (
  <Suspense fallback={<SubmitSpinner spinnerText={'Loading...'} />}>
    <CreateAccountEmailPreloader />
  </Suspense>
);

const router = createBrowserRouter([
  {
    path: '/login',
    element: <Root />,
    loader: loginRootLoader,
    children: [
      ...buildIntentLoginChildren(),
      {
        path: 'link/:oauthLinkActivationToken',
        element: <RequestLink />,
        loader: ({ params }) => {
          return loadQuery(RelayEnvironment, RequestLinkQuery, {
            token: params.oauthLinkActivationToken,
          });
        },
      },
      {
        path: 'link/link-account/:oauthLinkActivationToken',
        element: <LinkAccount />,
        loader: ({ params }) => {
          return loadQuery(RelayEnvironment, LinkAccountQuery, {
            token: params.oauthLinkActivationToken,
          });
        },
      },
      {
        path: 'change-password/:resetPasswordToken',
        element: (
          <Suspense fallback={<ChangePasswordGlimmer />}>
            <ChangePassword />
          </Suspense>
        ),
        loader: ({ params }) => {
          return loadQuery(RelayEnvironment, ChangePasswordQuery, {
            token: params.resetPasswordToken,
          });
        },
      },
      {
        path: 'activate/:activationKey',
        element: (
          <Suspense fallback={<ActivateGlimmer />}>
            <Activate />
          </Suspense>
        ),
        children: [
          {
            path: '',
            element: <ActivateOptions />,
          },
          {
            path: 'email',
            element: <ActivateEmail />,
            loader: ({ params }) => {
              return loadQuery(RelayEnvironment, ActivateEmailQuery, {
                key: params.activationKey,
              });
            },
          },
        ],
        loader: ({ params }) => {
          return loadQuery(RelayEnvironment, ActivateQuery, {
            key: params.activationKey,
          });
        },
      },
      { path: 'log-out', element: <Logout /> },
      {
        path: 'choose-role',
        element: (
          <Suspense fallback={<ChooseRoleGlimmer />}>
            <ChooseRole />
          </Suspense>
        ),
        loader: () => {
          return loadQuery(RelayEnvironment, ChooseRoleQuery, {});
        },
      },
    ],
  },
  {
    path: '/signup_agreement',
    element: <Root />,
    loader: loginRootLoader,
    children: [
      {
        index: true,
        element: <SignupAgreement />,
      },
    ],
  },
  {
    path: '/create-account',
    element: <Root />,
    loader: loginRootLoader,
    children: [
      {
        index: true,
        element: <CreateAccount />,
      },
      {
        path: 'email',
        element: createAccountEmailElement,
        loader: createAccountEmailLoader,
      },
      {
        path: 'login',
        element: <Outlet />,
        children: buildIntentLoginChildren(),
      },
    ],
  },
  {
    path: '/free-trial',
    element: <Root />,
    loader: loginRootLoader,
    children: [
      {
        index: true,
        element: <CreateAccount />,
      },
      {
        path: 'email',
        element: createAccountEmailElement,
        loader: createAccountEmailLoader,
      },
      {
        path: 'login',
        element: <Outlet />,
        children: buildIntentLoginChildren(),
      },
    ],
  },
]);

const App = () => <RouterProvider router={router} />;
if (rootElement) {
  renderReactRoot(rootElement, <App />);
}
